/**
 * Subscription widget web component.
 *
 * Flips four hidden inputs (`properties[_subscription_*]`) on the product
 * form between "disabled" (one-time) and "enabled" (subscribe). Inputs use
 * HTML5 `form="<id>"` so they submit with the form even though they live
 * outside it in the DOM.
 *
 * Writes `_subscription_consent_captured_at` at the moment the customer
 * picks "Subscribe" (not at widget render) so we have a true consent
 * timestamp, not a page-load timestamp.
 *
 * Companion backend: the `orders/create` webhook reads these properties
 * and creates a subscription row. If `_subscription_consent_captured_at`
 * is missing, the backend rejects sub creation with a loud log.
 */

import { Component } from '@theme/component';

/**
 * @typedef {Object} SubscriptionWidgetRefs
 * @property {HTMLInputElement} radioOnetime
 * @property {HTMLInputElement} radioSubscribe
 * @property {HTMLElement} frequencyWrap
 * @property {HTMLSelectElement} frequencySelect
 * @property {HTMLInputElement} propFrequency
 * @property {HTMLInputElement} propDiscount
 * @property {HTMLInputElement} propConsentAt
 * @property {HTMLInputElement} propConsentVersion
 * @property {HTMLInputElement} propPlanPublic
 */

const FREQUENCY_TO_LABEL = {
  biweekly: 'Every 2 weeks',
  monthly: 'Every month',
  bimonthly: 'Every 2 months',
  quarterly: 'Every 3 months',
};

function planLabel(frequency, discountPercent) {
  const freqLabel = FREQUENCY_TO_LABEL[frequency] ?? frequency;
  return `Subscribe & Save ${discountPercent}% · ${freqLabel}`;
}

/**
 * @extends {Component<SubscriptionWidgetRefs>}
 */
class SubscriptionWidgetComponent extends Component {
  requiredRefs = [
    'radioOnetime',
    'radioSubscribe',
    'frequencyWrap',
    'frequencySelect',
    'propFrequency',
    'propDiscount',
    'propConsentAt',
    'propConsentVersion',
    'propPlanPublic',
  ];

  connectedCallback() {
    super.connectedCallback();
    // Radio `change` doesn't fire reliably in the declarative `on:change=`
    // pattern on the parent label, so we wire it manually.
    this.refs.radioOnetime?.addEventListener('change', () =>
      this.#applyChoice('onetime'),
    );
    this.refs.radioSubscribe?.addEventListener('change', () =>
      this.#applyChoice('subscribe'),
    );
    this.#applyChoice(this.#currentChoice());
  }

  /**
   * Select `change` — only relevant when "Subscribe" is already picked.
   */
  handleFrequencyChange() {
    if (this.#currentChoice() !== 'subscribe') return;
    const select = this.refs.frequencySelect;
    if (select && this.refs.propFrequency) {
      this.refs.propFrequency.value = select.value;
    }
    if (select && this.refs.propPlanPublic && this.refs.propDiscount) {
      this.refs.propPlanPublic.value = planLabel(
        select.value,
        Number(this.refs.propDiscount.value ?? 0),
      );
    }
  }

  #currentChoice() {
    return this.refs.radioSubscribe?.checked ? 'subscribe' : 'onetime';
  }

  /**
   * @param {'onetime' | 'subscribe'} choice
   */
  #applyChoice(choice) {
    const isSubscribe = choice === 'subscribe';
    const {
      frequencyWrap,
      frequencySelect,
      propFrequency,
      propDiscount,
      propConsentAt,
      propConsentVersion,
      propPlanPublic,
    } = this.refs;

    // Toggle active class on each card — explicit rather than :has() so we
    // work on older Safari/older Chrome reliably.
    for (const label of this.querySelectorAll('.sub-widget__card')) {
      const labelChoice = label.dataset.choice;
      label.classList.toggle('sub-widget__card--active', labelChoice === choice);
    }

    if (frequencyWrap) {
      frequencyWrap.hidden = !isSubscribe;
    }

    const inputs = [
      propFrequency,
      propDiscount,
      propConsentAt,
      propConsentVersion,
      propPlanPublic,
    ];
    for (const input of inputs) {
      if (!input) continue;
      input.disabled = !isSubscribe;
    }

    if (isSubscribe) {
      if (propFrequency && frequencySelect) {
        propFrequency.value = frequencySelect.value;
      }
      if (propConsentAt) {
        propConsentAt.value = new Date().toISOString();
      }
      if (propPlanPublic && frequencySelect && propDiscount) {
        propPlanPublic.value = planLabel(
          frequencySelect.value,
          Number(propDiscount.value ?? 0),
        );
      }
    } else {
      if (propConsentAt) propConsentAt.value = '';
      if (propPlanPublic) propPlanPublic.value = '';
    }

    this.dispatchEvent(
      new CustomEvent('subscription-choice:change', {
        bubbles: true,
        detail: {
          choice,
          frequency: isSubscribe ? frequencySelect?.value ?? null : null,
          discountPercent: isSubscribe
            ? Number(propDiscount?.value ?? 0)
            : 0,
        },
      }),
    );
  }
}

if (!customElements.get('subscription-widget-component')) {
  customElements.define(
    'subscription-widget-component',
    SubscriptionWidgetComponent,
  );
}
